import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './Layout.jsx';
import { PlayerLayout } from './PlayerLayout.jsx';
import { NativeSplashReady } from './NativeSplashReady.jsx';
import { RequireAuth } from '../features/auth/components/RequireAuth.jsx';
import { RequireGuest } from '../features/auth/components/RequireGuest.jsx';
import { RequireOnboarding } from '../features/auth/components/RequireOnboarding.jsx';
import { NotFoundPage } from '../ui/NotFoundPage.jsx';
import { isB2BUser, resolveOnboardingPending } from '../features/auth/lib/onboardingStatus.js';
import { selectAuthUser, selectOnboardingPending } from '../features/auth/store/selectors.js';
import {
  selectCanShowCoachCalls,
  selectCanShowMyAssessments,
  selectCanShowMyCourses,
  selectCanShowSettings,
  selectCanShowSettingsHistory,
  selectDefaultLearnPath,
  selectFeatureVisibilityReady,
} from '../features/appVisibility/store/selectors.js';

function lazyNamed(loader, exportName) {
  return lazy(() => loader().then((module) => {
    const Page = module[exportName];
    return {
      // Only an actual page can release the splash: route guards and redirect
      // placeholders must not reveal an empty outlet while /mystep resolves.
      default: function ReadyPage(props) {
        return <NativeSplashReady><Page {...props} /></NativeSplashReady>;
      },
    };
  }));
}

const CoursesPage = lazyNamed(() => import('../features/courses/components/CoursesPage.jsx'), 'CoursesPage');
const CourseDetailPage = lazyNamed(() => import('../features/courses/components/CourseDetailPage.jsx'), 'CourseDetailPage');
const AssessmentsPage = lazyNamed(() => import('../features/courses/components/AssessmentsPage.jsx'), 'AssessmentsPage');
const UnitDetailPage = lazyNamed(() => import('../features/courses/components/UnitDetailPage.jsx'), 'UnitDetailPage');
const LessonPlayerPage = lazyNamed(() => import('../features/lesson/components/LessonPlayerPage.jsx'), 'LessonPlayerPage');
const CoachingCoursesPage = lazyNamed(() => import('../features/coaching/components/CoachingCoursesPage.jsx'), 'CoachingCoursesPage');
const CoachingPage = lazyNamed(() => import('../features/coaching/components/CoachingPage.jsx'), 'CoachingPage');
const IeltsCoachingPage = lazyNamed(() => import('../features/coaching/components/IeltsCoachingPage.jsx'), 'IeltsCoachingPage');
const SignInPage = lazyNamed(() => import('../features/auth/components/SignInPage.jsx'), 'SignInPage');
const SsoEntryPage = lazyNamed(() => import('../features/auth/components/SsoEntryPage.jsx'), 'SsoEntryPage');
const SsoCallbackPage = lazyNamed(() => import('../features/auth/components/SsoCallbackPage.jsx'), 'SsoCallbackPage');
const SignUpPage = lazyNamed(() => import('../features/auth/components/SignUpPage.jsx'), 'SignUpPage');
const OtpVerifyPage = lazyNamed(() => import('../features/auth/components/OtpVerifyPage.jsx'), 'OtpVerifyPage');
const ForgotPasswordPage = lazyNamed(() => import('../features/auth/components/ForgotPasswordPage.jsx'), 'ForgotPasswordPage');
const CheckEmailPage = lazyNamed(() => import('../features/auth/components/CheckEmailPage.jsx'), 'CheckEmailPage');
const OnboardingPage = lazyNamed(() => import('../features/auth/components/OnboardingPage.jsx'), 'OnboardingPage');
const LibraryPage = lazyNamed(() => import('../features/library/components/LibraryPage.jsx'), 'LibraryPage');
const ProductDetailPage = lazyNamed(() => import('../features/library/components/ProductDetailPage.jsx'), 'ProductDetailPage');
const PaymentProcessPage = lazyNamed(() => import('../features/library/components/PaymentProcessPage.jsx'), 'PaymentProcessPage');
const PaymentSuccessPage = lazyNamed(() => import('../features/library/components/PaymentSuccessPage.jsx'), 'PaymentSuccessPage');
const PaymentFailurePage = lazyNamed(() => import('../features/library/components/PaymentFailurePage.jsx'), 'PaymentFailurePage');
const AccountPage = lazyNamed(() => import('../features/account/components/AccountPage.jsx'), 'AccountPage');
const ProgressPage = lazyNamed(() => import('../features/progress/components/ProgressPage.jsx'), 'ProgressPage');
const LearningUnavailablePage = lazyNamed(
  () => import('../features/appVisibility/components/LearningUnavailablePage.jsx'),
  'LearningUnavailablePage',
);

function DefaultLearnRedirect() {
  const defaultPath = useSelector(selectDefaultLearnPath);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);
  if (!featureVisibilityReady) return null;
  return <Navigate to={defaultPath} replace />;
}

function LearnAreaRoute({ area, children }) {
  const canShowMyCourses = useSelector(selectCanShowMyCourses);
  const canShowMyAssessments = useSelector(selectCanShowMyAssessments);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);
  const defaultPath = useSelector(selectDefaultLearnPath);
  const canShowArea = area === 'assessments' ? canShowMyAssessments : canShowMyCourses;

  // Do not briefly render an unavailable area while /mystep is resolving.
  if (!featureVisibilityReady) return null;
  if (!canShowArea) return <Navigate to={defaultPath} replace />;
  return children;
}

function LearningUnavailableRoute() {
  const defaultPath = useSelector(selectDefaultLearnPath);
  const featureVisibilityReady = useSelector(selectFeatureVisibilityReady);

  if (!featureVisibilityReady) return null;
  if (defaultPath !== '/unavailable') return <Navigate to={defaultPath} replace />;
  return <LearningUnavailablePage />;
}

function RetailOnlyRoute({ children }) {
  const isB2B = useSelector((s) => isB2BUser({ user: s.auth.user, org: s.auth.org }));
  if (isB2B) return <DefaultLearnRedirect />;
  return children;
}

function SettingsOnlyRoute({ children }) {
  const canShowSettings = useSelector(selectCanShowSettings);
  return (
    <OnboardingCompleteRoute>
      {canShowSettings ? children : <DefaultLearnRedirect />}
    </OnboardingCompleteRoute>
  );
}

function OnboardingCompleteRoute({ children }) {
  const onboardingPending = useSelector(selectOnboardingPending);
  const user = useSelector(selectAuthUser);
  const effectiveOnboardingPending = resolveOnboardingPending({ onboardingPending, user });
  if (effectiveOnboardingPending) return <Navigate to="/onboarding" replace />;
  return children;
}

function CoachCallsRoute({ children }) {
  const canShowCoachCalls = useSelector(selectCanShowCoachCalls);
  if (!canShowCoachCalls) return <DefaultLearnRedirect />;
  return children;
}

function SettingsHistoryRoute() {
  const canShowHistory = useSelector(selectCanShowSettingsHistory);
  if (!canShowHistory) return <DefaultLearnRedirect />;
  return <ProgressPage />;
}

export function AppRouter() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/sso" element={<SsoCallbackPage />} />
        <Route
          path="/paymentfailure"
          element={<RetailOnlyRoute><PaymentFailurePage /></RetailOnlyRoute>}
        />
        <Route
          path="/paymentsuccess"
          element={<RetailOnlyRoute><PaymentSuccessPage /></RetailOnlyRoute>}
        />
        <Route
          path="/paymentprocess"
          element={<RequireAuth><RetailOnlyRoute><PaymentProcessPage /></RetailOnlyRoute></RequireAuth>}
        />

        <Route element={<RequireGuest />}>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signin/sso" element={<SsoEntryPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/verify" element={<OtpVerifyPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/forgot-password/sent" element={<CheckEmailPage />} />
        </Route>

        <Route element={<RequireAuth><Layout /></RequireAuth>}>
          <Route element={<RequireOnboarding />}>
            <Route path="/onboarding" element={<OnboardingPage />} />
          </Route>
          <Route index element={<DefaultLearnRedirect />} />
          <Route path="/courses" element={<LearnAreaRoute area="courses"><CoursesPage /></LearnAreaRoute>} />
          <Route path="/assessments" element={<LearnAreaRoute area="assessments"><AssessmentsPage /></LearnAreaRoute>} />
          <Route path="/assessments/:courseId" element={<LearnAreaRoute area="assessments"><CourseDetailPage area="assessments" /></LearnAreaRoute>} />
          <Route path="/assessments/:courseId/u/:unitId" element={<LearnAreaRoute area="assessments"><UnitDetailPage area="assessments" /></LearnAreaRoute>} />
          <Route path="/courses/:courseId" element={<LearnAreaRoute area="courses"><CourseDetailPage /></LearnAreaRoute>} />
          <Route path="/courses/:courseId/u/:unitId" element={<LearnAreaRoute area="courses"><UnitDetailPage /></LearnAreaRoute>} />
          <Route path="/coaching" element={<CoachCallsRoute><CoachingCoursesPage /></CoachCallsRoute>} />
          <Route path="/coaching/:courseId" element={<CoachCallsRoute><CoachingPage variant="generic" /></CoachCallsRoute>} />
          <Route path="/ielts-coaching/:courseId" element={<CoachCallsRoute><IeltsCoachingPage /></CoachCallsRoute>} />
          <Route path="/library" element={<RetailOnlyRoute><LibraryPage /></RetailOnlyRoute>} />
          <Route path="/library/:courseId" element={<RetailOnlyRoute><ProductDetailPage /></RetailOnlyRoute>} />
          <Route path="/account" element={<SettingsOnlyRoute><AccountPage /></SettingsOnlyRoute>} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/unavailable" element={<LearningUnavailableRoute />} />
          <Route path="/mydashboard" element={<DefaultLearnRedirect />} />
          <Route path="/mycourse" element={<DefaultLearnRedirect />} />
          <Route path="/profile" element={<SettingsOnlyRoute><Navigate to="/account" replace /></SettingsOnlyRoute>} />
          <Route path="/settings" element={<SettingsOnlyRoute><Navigate to="/account" replace /></SettingsOnlyRoute>} />
          <Route path="/history" element={<SettingsHistoryRoute />} />
          <Route path="/buycourse" element={<RetailOnlyRoute><Navigate to="/library" replace /></RetailOnlyRoute>} />
          <Route path="/freeactivities" element={<RetailOnlyRoute><Navigate to="/library" replace /></RetailOnlyRoute>} />
          <Route path="*" element={<NativeSplashReady><NotFoundPage /></NativeSplashReady>} />
        </Route>

        <Route element={<RequireAuth><PlayerLayout /></RequireAuth>}>
          <Route path="/learn/:courseId/:elementId" element={<LessonPlayerPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    </Suspense>
  );
}
